// SettingsModal.jsx - Cleaned and refactored component
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

// Reusable Toggle component
const Toggle = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between">
    <label className="text-gray-700 font-medium">{label}</label>
    <label className="inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
      />
      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                    peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full 
                    peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] 
                    after:start-[2px] after:bg-white after:border-gray-300 after:border 
                    after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600">
      </div>
    </label>
  </div>
);

// Reusable Radio component
const RadioGroup = ({ label, name, options, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-gray-700 font-medium block">{label}</label>
    <div className="flex space-x-4">
      {options.map(option => (
        <label key={option.value} className="inline-flex items-center">
          <input
            type="radio"
            className="form-radio text-teal-600"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="ml-2">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

export const SettingsModal = ({ isOpen, onClose, settings, onSettingsChange, currentLocation }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleToggleChange = (key) => {
    setLocalSettings({
      ...localSettings,
      [key]: !localSettings[key]
    });
  };

  const handleSelectChange = (key, value) => {
    setLocalSettings({
      ...localSettings,
      [key]: value
    });
  };

  const handleTextChange = (key, value) => {
    setLocalSettings({
      ...localSettings,
      [key]: value
    });
  };

  const handleSave = () => {
    onSettingsChange(localSettings);
    onClose();
  };

  // Check if we should show SNOMED codes option
  const shouldShowSnomedCodes = !['weho', 'brentwood'].includes(currentLocation?.toLowerCase());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Feature Toggles */}
          <div className="space-y-4">
            <Toggle
              label="Medical Assistant View (Disables note editing and disables all buttons)"
              checked={localSettings.maView}
              onChange={() => handleToggleChange('maView')}
            />

            <Toggle
              label="Suggest POC Tests"
              checked={localSettings.suggestPOCTests}
              onChange={() => handleToggleChange('suggestPOCTests')}
            />
            
            <Toggle 
              label="Show ICD-10 Codes" 
              checked={localSettings.showICDCodes} 
              onChange={() => handleToggleChange('showICDCodes')}
            />
            
            <Toggle
              label="Show CPT Codes"
              checked={localSettings.showCPTCodes}
              onChange={() => handleToggleChange('showCPTCodes')}
            />

            <Toggle
              label="Show E/M Codes"
              checked={localSettings.showEMCodes}
              onChange={() => handleToggleChange('showEMCodes')}
            />

            {shouldShowSnomedCodes && (
              <Toggle
                label="SNOMED Codes"
                checked={localSettings.showSnomedCodes}
                onChange={() => handleToggleChange('showSnomedCodes')}
              />
            )}
            
            <RadioGroup
              label="HPI Format"
              name="hpiFormat"
              options={[
                { value: 'paragraph', label: 'Paragraph' },
                { value: 'bullet', label: 'Bullet Points' }
              ]}
              value={localSettings.hpiFormat}
              onChange={(value) => handleSelectChange('hpiFormat', value)}
            />
            
            <Toggle 
              label="Assessment & Plan Style: Numbered Problems" 
              checked={localSettings.numberedAssessmentPlan} 
              onChange={() => handleToggleChange('numberedAssessmentPlan')}
            />
            
            <Toggle 
              label="Show Review of Systems (ROS)" 
              checked={localSettings.showROS} 
              onChange={() => handleToggleChange('showROS')}
            />
            
            <Toggle
              label="Show Physical Exam"
              checked={localSettings.showPhysicalExam}
              onChange={() => handleToggleChange('showPhysicalExam')}
            />

            <Toggle
              label="Show Additional MDM"
              checked={localSettings.showAdditionalMDM}
              onChange={() => handleToggleChange('showAdditionalMDM')}
            />
          </div>

          {/* Visit Summary Section */}
          <div className="border-t pt-4">
            <Toggle 
              label="Show Visit Summary" 
              checked={localSettings.showVisitSummary} 
              onChange={() => handleToggleChange('showVisitSummary')}
            />

            {localSettings.showVisitSummary && (
              <div className="mt-4 pl-4 space-y-4">
                <RadioGroup
                  label="Visit Summary Format"
                  name="visitSummaryFormat"
                  options={[
                    { value: 'sentence', label: 'Sentence' },
                    { value: 'bullet', label: 'Bullet Points' }
                  ]}
                  value={localSettings.visitSummaryFormat}
                  onChange={(value) => handleSelectChange('visitSummaryFormat', value)}
                />
                
                <Toggle 
                  label="Include Medications in Visit Summary" 
                  checked={localSettings.includeVisitSummaryMeds} 
                  onChange={() => handleToggleChange('includeVisitSummaryMeds')}
                />
              </div>
            )}
          </div>

                    {/* Custom Sign Off */}
                    <div className="space-y-2">
            <label className="text-gray-700 font-medium block">Custom Sign Off</label>
            <textarea
              className="w-full h-24 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:border-teal-500"
              placeholder="Enter custom sign off text to appear at the end of the Plan"
              value={localSettings.customSignOff}
              onChange={(e) => handleTextChange('customSignOff', e.target.value)}
            ></textarea>
          </div>

          {/* Language Selection */}
          {/*<div className="border-t pt-4 space-y-2">
            <label className="text-gray-700 font-medium block">Transcription Language</label>
            <select
              value={localSettings.transcriptionLanguage || 'en'}
              onChange={(e) => handleSelectChange('transcriptionLanguage', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="ru">Russian</option>
            </select>
            {<p className="text-sm text-gray-500">Default language for transcription.</p>}
          </div>*/}

          {/* Action Buttons */}
          <div className="border-t pt-4 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};